import type { StoreId, ResellerId, ChainId } from "@KassaroAB/shared/types/ids";
import { api } from "../utils/api";
import type { RestStore } from "@KassaroAB/shared/types/store";

export type StoreSearchKeyResponse = {
  searchKey: string;
  indexName: string;
  algoliaAppId: string;
};

export const getAdminStoreSearchKeyApi =
  async (): Promise<StoreSearchKeyResponse> => {
    return api.get(`admin/stores/search-key`);
  };

export const getResellerStoreSearchKeyApi = async (
  resellerId: ResellerId
): Promise<StoreSearchKeyResponse> => {
  return api.get(`resellers/${resellerId}/stores/search-key`);
};

export const getStoresApi = async (): Promise<RestStore[]> => {
  return api.get(`stores`);
};

export const getStoresPaginatedApi = async (cursor: string | null, limit: number = 20): Promise<RestStore[]> => {
  const params = new URLSearchParams();
  if (cursor) {
    params.set('cursor', cursor);
  }
  params.set('limit', limit.toString());
  return api.get(`admin/stores?${params.toString()}`);
};

export const createStoreApi = async (store: {
  name: string;
  displayName: string;
  street: string;
  postalcode: string;
  city: string;
  country: string;
  currency: string;
  language: string;
  timeZone?: string;
  orgNumber: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  active?: boolean;
}): Promise<RestStore> => {
  return api.post(`admin/stores`, store);
};

export const getStoreApi = async (storeId: StoreId): Promise<RestStore> => {
  return api.get(`stores/${storeId}`);
};

export const getResellerStoresPaginatedApi = async (resellerId: ResellerId, cursor: string | null, limit: number = 20): Promise<RestStore[]> => {
  const params = new URLSearchParams();
  if (cursor) {
    params.set('cursor', cursor);
  }
  params.set('limit', limit.toString());
  return api.get(`resellers/${resellerId}/stores?${params.toString()}`);
};

export const createResellerStoreApi = async (resellerId: ResellerId, store: {
  name: string;
  displayName: string;
  street: string;
  postalcode: string;
  city: string;
  country: string;
  currency: string;
  language: string;
  timeZone?: string;
  orgNumber: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  active?: boolean;
}): Promise<RestStore> => {
  return api.post(`resellers/${resellerId}/stores`, store);
};

export const getChainStoresPaginatedApi = async (chainId: ChainId, cursor: string | null, limit: number = 20): Promise<RestStore[]> => {
  const params = new URLSearchParams();
  if (cursor) {
    params.set('cursor', cursor);
  }
  params.set('limit', limit.toString());
  return api.get(`chains/${chainId}/stores?${params.toString()}`);
};

export const createChainStoreApi = async (chainId: ChainId, store: {
  name: string;
  displayName: string;
  street: string;
  postalcode: string;
  city: string;
  country: string;
  currency: string;
  language: string;
  timeZone?: string;
  orgNumber: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  active?: boolean;
}): Promise<RestStore> => {
  return api.post(`chains/${chainId}/stores`, store);
};

export type StoreInfoPatch = Partial<{
  name: string;
  displayName?: string;
  street: string;
  postalcode: string;
  city: string;
  orgNumber?: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  active: boolean;
}>;

export const updateStoreSettingsApi = async (
  storeId: StoreId,
  body: StoreInfoPatch,
): Promise<null> => {
  return api.put(`stores/${storeId}`, body);
};